import React, { useEffect, useState } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Autocomplete, IconButton, Typography } from '@mui/material';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { DataGrid } from '@mui/x-data-grid';
import { CSVLink } from 'react-csv';
import dayjs from 'dayjs';

import { getBillRegister } from '../lib/api-bill';
import { getAllCustomersAuto } from '../lib/api-customer';
import LoadingSpinner from '../components/common/LoadingSpinner';

function BillRegister() {
    const [rows, setRows] = useState([]);
    const [cust, setCust] = useState([]);
    const [startDate, setStartDate] = useState(dayjs());
    const [endDate, setEndDate] = useState(dayjs());
    const [custName, setCustName] = useState(null);

    const [fullyPaid, setFullyPaid] = useState(true);
    const [partialPaid, setPartialPaid] = useState(true);
    const [pending, setPending] = useState(true);

    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchCustomers = async () => {
            try {
                const response = await getAllCustomersAuto();
                const customerOptions = response.map((row) => ({
                    value: row.Cust_Id,
                    label: row.Cust_Name
                }));
                setCust(customerOptions);
            } catch (err) {
                console.error('Error fetching customers:', err);
            }
        };

        fetchCustomers();
    }, []);

    const handleApplyFilter = async () => {
        try {
            setLoading(true);
            setError('');

            const selectedStatuses = [];
            if (fullyPaid) selectedStatuses.push('Fully Paid');
            if (partialPaid) selectedStatuses.push('Partial Paid');
            if (pending) selectedStatuses.push('Pending');

            const filterCriteria = {
                fromDate: startDate.format('DD/MM/YYYY'),
                toDate: endDate.format('DD/MM/YYYY'),
                statuses: selectedStatuses
            };

            if (custName) {
                filterCriteria.cName = custName;
            }

            const response = await getBillRegister(filterCriteria);

            const searchedData = response.data.map((row, index) => ({
                id: row.id,
                srNo: index + 1,
                ...row
            }));

            setRows(searchedData);
        } catch (err) {
            console.error('Error fetching Bill Register:', err);
            setError(
                err.response?.data?.message ||
                'Error fetching Bill Register data'
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        // Keep the table in sync immediately when a payment-status checkbox changes.
        handleApplyFilter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullyPaid, partialPaid, pending]);

    const columns = [
        { field: 'srNo', headerName: 'Sr. No.', flex: 0.7 },
        { field: 'CustomerName', headerName: 'Customer Name', flex: 1.5 },
        { field: 'Bill_no', headerName: 'Bill No', flex: 1 },
        { field: 'Bill_Date', headerName: 'Bill Date', flex: 1 },
        { field: 'BillAmount', headerName: 'Bill Amount', flex: 1 },
        { field: 'TotalReceived', headerName: 'Total Received', flex: 1.1 },
        { field: 'Adjustment', headerName: 'Adjustment', flex: 1 },
        { field: 'Balance', headerName: 'Balance', flex: 1 }
    ];

    return (
        <div>
            {loading && <LoadingSpinner />}

            <h1
                style={{
                    fontFamily: 'Overpass, serif',
                    textAlign: 'center',
                    fontSize: '2rem'
                }}
            >
                Bill Register
            </h1>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2rem',
                    marginInline: '5%',
                    marginBlock: '4%'
                }}
            >
                {/* Start Date / End Date */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-evenly'
                    }}
                >
                    <div>
                        <FormControl>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={startDate}
                                    onChange={(date) => setStartDate(date)}
                                    format="DD/MM/YYYY"
                                    label="Start Date"
                                />
                            </LocalizationProvider>
                        </FormControl>
                    </div>

                    <div>
                        <FormControl>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    value={endDate}
                                    onChange={(date) => setEndDate(date)}
                                    format="DD/MM/YYYY"
                                    label="End Date"
                                />
                            </LocalizationProvider>
                        </FormControl>
                    </div>
                </div>

                {/* Customer Name / Search */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-evenly',
                        marginLeft: '14%'
                    }}
                >
                    <Autocomplete
                        options={cust}
                        value={custName}
                        onChange={(event, newValue) => setCustName(newValue)}
                        getOptionLabel={(option) => option.label}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Customer Name"
                                sx={{ width: '47vw' }}
                            />
                        )}
                    />

                    <IconButton aria-label="search">
                        <Button
                            color="error"
                            variant="contained"
                            onClick={handleApplyFilter}
                        >
                            Search
                        </Button>
                    </IconButton>
                </div>

                {/* Payment status filters */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        marginTop: '-1rem'
                    }}
                >
                    <FormGroup row>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={fullyPaid}
                                    onChange={(e) =>
                                        setFullyPaid(e.target.checked)
                                    }
                                    color="primary"
                                />
                            }
                            label="Fully Paid"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={partialPaid}
                                    onChange={(e) =>
                                        setPartialPaid(e.target.checked)
                                    }
                                    color="primary"
                                />
                            }
                            label="Partial Paid"
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={pending}
                                    onChange={(e) =>
                                        setPending(e.target.checked)
                                    }
                                    color="primary"
                                />
                            }
                            label="Pending"
                        />
                    </FormGroup>
                </div>

                {error && (
                    <Typography
                        color="error"
                        sx={{ textAlign: 'center', marginTop: '-1rem' }}
                    >
                        {error}
                    </Typography>
                )}

                {/* Export */}
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <CSVLink
                        data={rows.map((row) =>
                            columns.map((col) => {
                                if (col.field === 'Bill_Date' && row[col.field]) {
                                    const parsed = dayjs(row[col.field]);
                                    return parsed.isValid()
                                        ? "'" + parsed.format('DD/MM/YYYY')
                                        : row[col.field];
                                }
                                return row[col.field];
                            })
                        )}
                        headers={columns.map((column) => column.headerName)}
                        separator=","
                        filename="bill_register.csv"
                        style={{ textDecoration: 'none' }}
                    >
                        <Button
                            color="success"
                            variant="contained"
                            endIcon={<SaveAltIcon />}
                        >
                            Export to CSV
                        </Button>
                    </CSVLink>
                </div>
            </div>

            {/* Bill Register table */}
            <div
                style={{
                    paddingTop: '20px',
                    height: 400,
                    width: '100%'
                }}
            >
                <DataGrid
                    density="compact"
                    rows={rows}
                    columns={columns}
                    pageSize={5}
                    rowsPerPageOptions={[5, 10, 20]}
                />
            </div>
        </div>
    );
}

export default BillRegister;
